import type { QueryClient } from '@tanstack/react-query';
import { data, redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { recognizeOptionsQuery } from '../api';
import { requireMe } from '../session/loaders';

/**
 * Help topics, the Help chat and the email assistant link to an award type by name
 * (`/recognize/taa`), so a link survives changes to Recognize's own address. Recognize is one page
 * (`/recognize?type=TAA&sector=…`): these addresses open the entry when the person has exactly one
 * of that type, and the type picker otherwise.
 */
const KINDS: Readonly<Record<string, 'SDB' | 'PAA' | 'TAA' | 'EDB' | null>> = {
  'sector-bonus': 'SDB',
  paa: 'PAA',
  taa: 'TAA',
  'executive-bonus': 'EDB',
  // A Spot nomination starts on Recognize itself.
  spot: null,
};

export function recognizeLinkTarget(
  kind: string,
  entries: readonly { type: string; sector: { id: string } | null }[],
): string | null {
  if (!Object.hasOwn(KINDS, kind)) return null;
  const type = KINDS[kind];
  if (type === null || type === undefined) return '/recognize';
  const ofType = entries.filter((e) => e.type === type);
  const only = ofType[0];
  if (only === undefined || (ofType.length > 1 && type !== 'EDB')) return '/recognize';
  const params = new URLSearchParams({ type });
  if (only.sector !== null) params.set('sector', only.sector.id);
  return `/recognize?${params.toString()}`;
}

export function helpLinkRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    {
      path: 'recognize/:kind',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        const options = await queryClient.query(recognizeOptionsQuery);
        const target = recognizeLinkTarget(params['kind'] ?? '', options.entries);
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
        if (target === null) throw data(null, { status: 404 });
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router redirects by throwing
        throw redirect(target);
      },
    },
  ];
}
